import type { GridApi, IRowNode } from 'ag-grid-community';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { setAlpha } from '../../../styles/utils';
import { Flex } from '../../Core';
import { BOTTOM_SCROLL_GRADIENT_HEIGHT } from '../../Mobile';
import type { RenderCellOptions } from '../useRenderCell';
import { MobileRowDetailRows } from './MobileRowDetailRows';

export interface MobileRowDetailSheetProps<TData> {
  api: GridApi<TData>;
  node: IRowNode<TData> | undefined;
  renderCell: (colId: string, options?: RenderCellOptions) => ReactNode;
  excludeColumnIds?: string[];
  /**
   * Optional content rendered in a sticky footer below the scrollable rows.
   * Intended for action buttons (e.g. Cancel, Resubmit). Receives the full
   * width of the sheet; the parent is a `Flex` with `gap="spacing16"` so
   * multiple buttons naturally fill the row.
   *
   * Receives the latest row data from the current node on each re-render.
   */
  footerRenderer?: (data: TData) => ReactNode;
  /**
   * Optional content rendered above the column detail rows.
   *
   * Receives the latest row data from the current node on each re-render.
   */
  headerRenderer?: (data: TData) => ReactNode;
}

/**
 * Generic detail sheet for a single blotter row. Renders every visible column
 * as a label/value pair using existing cell renderers — consistent with the
 * desktop blotter column config and formatting.
 *
 * The optional `footer` is rendered in a sticky area below the scrollable
 * rows so action buttons stay anchored to the bottom regardless of content length.
 *
 * Designed to be passed as `content` to {@link MobileModalProvider}'s
 * `openModal`.
 */
export function MobileRowDetailSheet<TData>({
  api,
  node,
  renderCell,
  excludeColumnIds = ['warning', 'reconWarning', 'creditUsageWarning'],
  headerRenderer,
  footerRenderer,
}: MobileRowDetailSheetProps<TData>) {
  const [, setRenderVersion] = useState(0);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const header = node?.data == null ? null : headerRenderer?.(node.data);
  const footer = node?.data == null ? null : footerRenderer?.(node.data);

  useEffect(() => {
    if (node == null) {
      return;
    }

    const forceRerender = () => {
      setRenderVersion(prev => prev + 1);
    };

    node.addEventListener('cellChanged', forceRerender);
    node.addEventListener('dataChanged', forceRerender);

    return () => {
      node.removeEventListener('cellChanged', forceRerender);
      node.removeEventListener('dataChanged', forceRerender);
    };
  }, [node]);

  useEffect(() => {
    if (footer == null) {
      setFooterHeight(0);
      return;
    }

    const footerElement = footerRef.current;
    if (footerElement == null) {
      return;
    }

    const updateHeight = () => {
      setFooterHeight(footerElement.offsetHeight);
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerElement);

    return () => {
      observer.disconnect();
    };
  }, [footer]);

  return (
    <Flex flexDirection="column" w="100%" gap="spacing12" position="relative" minHeight="100%">
      {header}

      {node != null && (
        <MobileRowDetailRows
          api={api}
          renderCell={renderCell}
          excludeColumnIds={excludeColumnIds}
          footerHeight={footerHeight}
        />
      )}

      {/* Sticky footer — optional action buttons */}
      {footer != null && <FooterArea ref={footerRef}>{footer}</FooterArea>}
    </Flex>
  );
}

const FooterArea = styled(Flex)`
  position: sticky;
  bottom: 0;
  flex-shrink: 0;
  z-index: 1;
  margin-top: auto;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: ${BOTTOM_SCROLL_GRADIENT_HEIGHT / 2}px;
    pointer-events: none;
    background: linear-gradient(
      to top,
      ${({ theme }) => theme.backgroundContent},
      ${({ theme }) => setAlpha(0, theme.backgroundContent)}
    );
  }
`;
